import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Mail, X, AlertTriangle, ArrowRight, Trash2, Check, RefreshCw, Copy, CheckCheck } from 'lucide-react';
import { webvonkClient } from '../lib/supabase';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const ACCENT = '#f97316';

const Wrap = styled.div`
  height: 100%;
  background: ${p => p.theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const Content = styled.div`
  padding: 40px 20px 40px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  max-width: 900px;
  margin: 0 auto;
  animation: ${fadeUp} 0.3s ease both;
  @media (max-width: 600px) { padding: 24px 14px 32px; }
`;

const PageHeader = styled.div`
  padding: 8px 0 20px;
  h1 {
    font-size: clamp(22px, 3vw, 28px);
    font-weight: 700;
    color: ${p => p.theme.colors.text};
    letter-spacing: -0.02em;
    margin: 0;
  }
`;

const PageSubRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
`;

const RefreshBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.theme.colors.muted};
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
  &:hover { background: ${p => p.theme.colors.surface}; color: ${p => p.theme.colors.text}; }
`;

const NavBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  overflow-x: auto;
  padding-bottom: 4px;
  &::-webkit-scrollbar { display: none; }
`;

const NavTab = styled.button`
  flex: 1;
  padding: 8px 14px;
  background: ${p => p.$active ? `${p.$color}1e` : p.theme.colors.surface};
  color: ${p => p.$active ? p.$color : p.theme.colors.muted};
  border: 1px solid ${p => p.$active ? `${p.$color}44` : p.theme.colors.border};
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  &:hover { background: ${p => p.$active ? `${p.$color}2a` : p.theme.colors.background}; }
`;

const TabCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  opacity: ${p => p.$active ? 1 : 0.55};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
  padding-bottom: 40px;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(0,0,0,0.12); }
  &:active { transform: scale(0.99); }
`;

const CardHeader = styled.div`
  padding: 14px 16px 10px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const CardName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardDate = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
  opacity: 0.5;
  white-space: nowrap;
  flex-shrink: 0;
`;

const CardEmail = styled.div`
  padding: 0 16px 8px;
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
  display: flex;
  align-items: center;
  gap: 5px;
`;

const CardMessage = styled.div`
  margin: 0 16px 12px;
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
  line-height: 1.55;
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 10px;
  padding: 10px 12px;
  white-space: pre-wrap;
  ${p => p.$truncated && `
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px 12px;
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const StatusBadge = styled.div`
  flex: 1;
  font-size: 11px;
  font-weight: 700;
  color: ${p => p.$color};
`;

const ActionBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${p => p.$danger ? 'rgba(239,68,68,0.3)' : `${p.$color}44`};
  background: ${p => p.$danger ? 'rgba(239,68,68,0.1)' : `${p.$color}18`};
  color: ${p => p.$danger ? '#ef4444' : p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  &:hover { transform: scale(1.1); background: ${p => p.$danger ? 'rgba(239,68,68,0.2)' : `${p.$color}30`}; }
  &:active { transform: scale(0.95); }
`;

const EmptyState = styled.div`
  padding: 60px 24px;
  text-align: center;
  color: ${p => p.theme.colors.muted};
`;
const EmptyTitle = styled.div`font-size: 0.92rem; font-weight: 600; color: ${p => p.theme.colors.text}; opacity: 0.5; margin-bottom: 4px;`;
const EmptyText = styled.div`font-size: 0.78rem; opacity: 0.35;`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.35);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 2000;
  @media (min-width: 600px) { align-items: center; padding: 24px; }
`;

const Modal = styled.div`
  background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.72)'};
  backdrop-filter: blur(32px) saturate(200%);
  -webkit-backdrop-filter: blur(32px) saturate(200%);
  border: 1px solid ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.6)'};
  border-radius: 24px 24px 0 0;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px 22px 36px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.15);
  @media (min-width: 600px) { border-radius: 24px; max-width: 540px; }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 12px;
`;

const ModalName = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  letter-spacing: -0.02em;
  margin: 0 0 3px;
`;

const ModalDate = styled.div`font-size: 0.72rem; color: ${p => p.theme.colors.muted}; opacity: 0.5;`;

const CloseBtn = styled.button`
  width: 32px; height: 32px;
  border-radius: 50%; border: none;
  background: ${p => p.theme.colors.border};
  color: ${p => p.theme.colors.muted};
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; flex-shrink: 0;
  &:hover { background: ${p => p.theme.colors.text}20; }
`;

const ModalSection = styled.div`margin-bottom: 18px;`;

const ModalLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.muted};
  opacity: 0.5;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalValue = styled.div`
  font-size: 0.88rem;
  color: ${p => p.theme.colors.text};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ModalMessage = styled.div`
  font-size: 0.85rem;
  color: ${p => p.theme.colors.text};
  line-height: 1.65;
  background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'};
  border: 1px solid ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'};
  border-radius: 12px;
  padding: 14px 16px;
  white-space: pre-wrap;
`;

const StatusSelect = styled.select`
  padding: 9px 13px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 10px;
  font-size: 0.85rem;
  color: ${p => p.theme.colors.text};
  background: ${p => p.theme.colors.background};
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-bottom: 16px;
  &:focus { outline: none; border-color: ${ACCENT}; }
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const FooterBtn = styled.button`
  flex: 1;
  padding: 11px;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.15s;
  border: 1px solid ${p => p.$danger ? 'rgba(239,68,68,0.3)' : p.$primary ? 'transparent' : p.theme.colors.border};
  background: ${p => p.$danger ? 'rgba(239,68,68,0.08)' : p.$primary ? ACCENT : p.theme.colors.surface};
  color: ${p => p.$danger ? '#ef4444' : p.$primary ? '#fff' : p.theme.colors.text};
  &:hover { background: ${p => p.$danger ? 'rgba(239,68,68,0.16)' : p.$primary ? '#8a18aa' : p.theme.colors.border}; }
`;

const ConfirmBox = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 20px;
  padding: 28px 24px 24px;
  max-width: 360px;
  width: 90%;
  text-align: center;
  box-shadow: 0 24px 64px rgba(0,0,0,0.25);
`;

const ConfirmIconWrap = styled.div`
  width: 52px; height: 52px; border-radius: 50%;
  background: rgba(239,68,68,0.1);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 16px; color: #ef4444;
`;

const ConfirmTitle = styled.h3`font-size: 1rem; font-weight: 700; color: ${p => p.theme.colors.text}; margin: 0 0 8px;`;
const ConfirmText = styled.p`font-size: 0.82rem; color: ${p => p.theme.colors.muted}; opacity: 0.65; margin: 0 0 24px; line-height: 1.5;`;
const ConfirmActions = styled.div`display: flex; gap: 10px;`;
const BtnSecondary = styled.button`
  flex: 1; padding: 10px; border-radius: 50px;
  border: 1px solid ${p => p.theme.colors.border};
  background: transparent; color: ${p => p.theme.colors.text};
  font-size: 0.85rem; font-weight: 600; cursor: pointer; font-family: inherit;
  &:hover { background: ${p => p.theme.colors.border}; }
`;
const BtnDanger = styled.button`
  flex: 1; padding: 10px; border-radius: 50px; border: none;
  background: #ef4444; color: #fff;
  font-size: 0.85rem; font-weight: 600; cursor: pointer; font-family: inherit;
  &:hover { background: #dc2626; }
`;

const STATUS_COLOR = { Nieuw: ACCENT, Bezig: '#3b82f6', Beantwoord: '#22c55e' };
const NEXT_STATUS  = { Nieuw: 'Bezig', Bezig: 'Beantwoord' };

const TABS = [
  { key: 'Nieuw',      color: ACCENT },
  { key: 'Bezig',      color: '#3b82f6' },
  { key: 'Beantwoord', color: '#22c55e' },
];

function fmtDate(iso) {
  return new Date(iso).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function fmtDateLong(iso) {
  return new Date(iso).toLocaleString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function WhenconnectContact() {
  const [tab, setTab]                     = useState('Nieuw');
  const [all, setAll]                     = useState([]);
  const [counts, setCounts]               = useState({});
  const [loading, setLoading]             = useState(true);
  const [selected, setSelected]           = useState(null);
  const [copied, setCopied]               = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [lastFetched, setLastFetched]     = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    const { data } = await webvonkClient
      .from('wc_contacten')
      .select('*')
      .order('ingediend_op', { ascending: false });
    const rows = data ?? [];
    setAll(rows);
    const c = {};
    TABS.forEach(t => { c[t.key] = rows.filter(r => (r.status ?? 'Nieuw') === t.key).length; });
    setCounts(c);
    setLastFetched(new Date());
    setLoading(false);
  };

  const filtered = all.filter(r => (r.status ?? 'Nieuw') === tab);

  const updateStatus = async (id, status, e) => {
    e?.stopPropagation();
    await webvonkClient.from('wc_contacten').update({ status }).eq('id', id);
    setSelected(p => p ? { ...p, status } : null);
    await loadAll();
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    await webvonkClient.from('wc_contacten').delete().eq('id', confirmDelete);
    setConfirmDelete(null);
    setSelected(null);
    await loadAll();
  };

  return (
    <Wrap>
      <Content>
        <PageHeader>
          <PageSubRow>
            <h1 style={{ margin: 0 }}>Contactinzendingen</h1>
            <RefreshBtn onClick={loadAll}>
              <RefreshCw size={11} />
              {lastFetched
                ? lastFetched.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })
                : 'Verversen'}
            </RefreshBtn>
          </PageSubRow>
        </PageHeader>

        <NavBar>
          {TABS.map(({ key, color }) => (
            <NavTab key={key} $active={tab === key} $color={color} onClick={() => setTab(key)}>
              {key}
              <TabCount $active={tab === key}>{counts[key] ?? 0}</TabCount>
            </NavTab>
          ))}
        </NavBar>

        {loading ? (
          <EmptyState><EmptyText>Laden…</EmptyText></EmptyState>
        ) : filtered.length === 0 ? (
          <EmptyState>
            <EmptyTitle>Geen inzendingen</EmptyTitle>
            <EmptyText>Er zijn geen {tab.toLowerCase()} berichten.</EmptyText>
          </EmptyState>
        ) : (
          <Grid>
            {filtered.map(c => {
              const accent = STATUS_COLOR[c.status ?? 'Nieuw'];
              const next = NEXT_STATUS[c.status ?? 'Nieuw'];
              return (
                <Card key={c.id} onClick={() => setSelected(c)}>
                  <CardHeader>
                    <div style={{ minWidth: 0 }}>
                      <CardName>{c.naam}</CardName>
                    </div>
                    <CardDate>{fmtDate(c.ingediend_op)}</CardDate>
                  </CardHeader>
                  <CardEmail><Mail size={11} />{c.email}</CardEmail>
                  <CardMessage $truncated={c.bericht?.length > 350}>
                    {c.bericht}
                  </CardMessage>
                  <CardFooter>
                    <StatusBadge $color={accent}>{c.status ?? 'Nieuw'}</StatusBadge>
                    <ActionBtn $color="#ef4444" $danger title="Verwijderen" onClick={e => { e.stopPropagation(); setConfirmDelete(c.id); }}>
                      <Trash2 size={13} />
                    </ActionBtn>
                    {next && (
                      <ActionBtn $color={STATUS_COLOR[next]} title={next === 'Bezig' ? 'In behandeling' : 'Beantwoord'} onClick={e => updateStatus(c.id, next, e)}>
                        {next === 'Beantwoord' ? <Check size={13} /> : <ArrowRight size={13} />}
                      </ActionBtn>
                    )}
                  </CardFooter>
                </Card>
              );
            })}
          </Grid>
        )}
      </Content>

      {selected && (
        <Overlay onClick={() => setSelected(null)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalName>{selected.naam}</ModalName>
                <ModalDate>{fmtDateLong(selected.ingediend_op)}</ModalDate>
              </div>
              <CloseBtn onClick={() => setSelected(null)}><X size={15} /></CloseBtn>
            </ModalHeader>

            <ModalSection>
              <ModalLabel>E-mailadres</ModalLabel>
              <ModalValue><Mail size={14} /> {selected.email}</ModalValue>
            </ModalSection>

            <ModalSection>
              <ModalLabel>
                <span>Bericht</span>
                <button
                  onClick={() => { navigator.clipboard.writeText(selected.bericht); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.68rem', fontWeight: 600, fontFamily: 'inherit', color: copied ? '#22c55e' : 'inherit', opacity: copied ? 1 : 0.6, textTransform: 'uppercase', letterSpacing: '0.06em', padding: 0 }}
                >
                  {copied ? <CheckCheck size={11} /> : <Copy size={11} />}
                  {copied ? 'Gekopieerd' : 'Kopiëren'}
                </button>
              </ModalLabel>
              <ModalMessage>{selected.bericht}</ModalMessage>
            </ModalSection>

            <ModalSection>
              <ModalLabel>Status</ModalLabel>
              <StatusSelect value={selected.status ?? 'Nieuw'} onChange={e => updateStatus(selected.id, e.target.value)}>
                <option value="Nieuw">Nieuw</option>
                <option value="Bezig">Bezig</option>
                <option value="Beantwoord">Beantwoord</option>
              </StatusSelect>
            </ModalSection>

            <ModalFooter>
              <FooterBtn $danger onClick={() => { setConfirmDelete(selected.id); setSelected(null); }}>
                <Trash2 size={14} /> Verwijderen
              </FooterBtn>
              {NEXT_STATUS[selected.status ?? 'Nieuw'] && (
                <FooterBtn $primary onClick={() => updateStatus(selected.id, NEXT_STATUS[selected.status ?? 'Nieuw'])}>
                  <ArrowRight size={14} />
                  {NEXT_STATUS[selected.status ?? 'Nieuw'] === 'Bezig' ? 'In behandeling' : 'Beantwoord'}
                </FooterBtn>
              )}
              <FooterBtn onClick={() => setSelected(null)}>Sluiten</FooterBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}

      {confirmDelete && (
        <Overlay onClick={() => setConfirmDelete(null)}>
          <ConfirmBox onClick={e => e.stopPropagation()}>
            <ConfirmIconWrap><AlertTriangle size={24} /></ConfirmIconWrap>
            <ConfirmTitle>Verwijderen?</ConfirmTitle>
            <ConfirmText>Dit bericht wordt permanent verwijderd en kan niet worden teruggehaald.</ConfirmText>
            <ConfirmActions>
              <BtnSecondary onClick={() => setConfirmDelete(null)}>Annuleren</BtnSecondary>
              <BtnDanger onClick={doDelete}>Verwijderen</BtnDanger>
            </ConfirmActions>
          </ConfirmBox>
        </Overlay>
      )}
    </Wrap>
  );
}
